import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Window } from '../Window/Window';
import { SaveDialog } from './SaveDialog';

const MenuBar = styled.div`
  display: flex;
  background: #c0c0c0;
  padding: 2px;
  border-bottom: 1px solid #808080;
`;

const MenuItem = styled.div`
  position: relative;
  padding: 2px 8px;
  cursor: pointer;
  user-select: none;
  
  &:hover {
    background: #000080;
    color: white;
  }
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: #c0c0c0;
  border: 1px solid #808080;
  display: ${props => props.isOpen ? 'block' : 'none'};
  z-index: 1000;
`;

const MenuOption = styled.div`
  padding: 4px 20px;
  white-space: nowrap;
  
  &:hover {
    background: #000080;
    color: white;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  height: calc(100% - 25px);
  resize: none;
  padding: 4px;
  font-family: ${props => props.theme.fontFamily || 'MS Sans Serif'};
  border: none;
  outline: none;
  background-color: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-size: ${props => props.theme.fontSize || '14px'};
  text-shadow: ${props => props.theme.textShadow || 'none'};
  animation: ${props => props.theme.animation || 'none'};

  @keyframes rainbow {
    0% { color: #ff0000; }
    20% { color: #ffff00; }
    40% { color: #00ff00; }
    60% { color: #00ffff; }
    80% { color: #ff00ff; }
    100% { color: #ff0000; }
  }

  @keyframes matrix {
    0% { text-shadow: 0 0 5px #00ff00; }
    50% { text-shadow: 0 0 10px #00ff00, 0 0 20px #00ff00; }
    100% { text-shadow: 0 0 5px #00ff00; }
  }

  @keyframes disco {
    0% { background-color: #ff0000; }
    33% { background-color: #00ff00; }
    66% { background-color: #0000ff; }
    100% { background-color: #ff0000; }
  }
`;

const StatusBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 20px;
  background: #c3c3c3;
  padding: 2px 5px;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
`;

const GameContainer = styled.div`
  position: absolute;
  top: 25px;
  left: 0;
  right: 0;
  bottom: 20px;
  background: black;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const GameCanvas = styled.canvas`
  border: 2px solid #00ff00;
  image-rendering: pixelated;
  max-width: 100%;
  max-height: 100%;
`;

const GameScore = styled.div`
  color: #00ff00;
  font-family: 'Press Start 2P', monospace;
  font-size: 12px;
  text-align: center;
  margin-top: 10px;
`;

const asciiArt = {
  dog: `
    ∩――――∩
    ||  ･ω･||
    ||     ||
    |｜    |｜
    Ｕ￣￣￣Ｕ
  `,
  cat: `
    /\\___/\\
   (  o o  )
   (  =^=  ) 
    (--m--)
  `,
  windows: `
    ████████████████
    ████████████████
    ████      ██████
    ████      ██████
    ████      ██████
    ████      ██████
    ████████████████
    ████████████████
  `
};

const themes = {
  matrix: {
    background: 'black',
    color: '#00ff00',
    effects: `text-shadow: 0 0 5px #00ff00;`
  },
  vaporwave: {
    background: 'linear-gradient(to right, #ff71ce, #01cdfe, #05ffa1)',
    color: 'white',
    effects: `text-shadow: 2px 2px #ff00ff;`
  },
  classic: {
    background: 'white',
    color: 'black',
    effects: ''
  }
};

const SnakeGame = ({ onClose }) => {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  
  const GRID_SIZE = 15;
  const CELL_SIZE = 12;
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let snake = [{ x: 7, y: 7 }]; // Start more centered
    let food = { x: 11, y: 11 }; // Initial food position
    let direction = 'right';
    let gameLoop;

    const drawCell = (x, y, color) => {
      ctx.fillStyle = color;
      ctx.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE - 1, CELL_SIZE - 1);
    };

    const generateFood = () => {
      // Keep generating new positions until we find one not occupied by the snake
      let newFood;
      do {
        newFood = {
          x: Math.floor(Math.random() * GRID_SIZE),
          y: Math.floor(Math.random() * GRID_SIZE)
        };
      } while (snake.some(segment => segment.x === newFood.x && segment.y === newFood.y));
      
      food = newFood;
    };

    const draw = () => {
      // Clear canvas
      ctx.fillStyle = 'black';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw snake
      snake.forEach((segment) => {
        drawCell(segment.x, segment.y, '#00ff00');
      });

      // Draw food - make sure this happens after clearing the canvas
      drawCell(food.x, food.y, '#ff0000');
    };

    const update = () => {
      if (gameOver) return;

      const head = { ...snake[0] };

      // Update head position based on direction
      switch (direction) {
        case 'up': head.y--; break;
        case 'down': head.y++; break;
        case 'left': head.x--; break;
        case 'right': head.x++; break;
        default: break;
      }

      // Check for collisions with walls
      if (head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE) {
        setGameOver(true);
        clearInterval(gameLoop);
        return;
      }

      // Check for collisions with self
      if (snake.some(segment => segment.x === head.x && segment.y === head.y)) {
        setGameOver(true);
        clearInterval(gameLoop);
        return;
      }

      // Add new head
      snake.unshift(head);

      // Check if food was eaten
      if (head.x === food.x && head.y === food.y) {
        setScore(prev => prev + 10);
        generateFood(); // Generate new food immediately after eating
      } else {
        snake.pop(); // Remove tail if no food was eaten
      }

      draw();
    };

    // Handle keyboard input
    const handleKeyPress = (e) => {
      e.preventDefault(); // Prevent scrolling with arrow keys
      
      switch (e.key) {
        case 'ArrowUp':
          if (direction !== 'down') direction = 'up';
          break;
        case 'ArrowDown':
          if (direction !== 'up') direction = 'down';
          break;
        case 'ArrowLeft':
          if (direction !== 'right') direction = 'left';
          break;
        case 'ArrowRight':
          if (direction !== 'left') direction = 'right';
          break;
        default:
          break;
      }
    };

    // Initial draw
    draw();
    
    // Start game loop
    document.addEventListener('keydown', handleKeyPress);
    gameLoop = setInterval(update, 100);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      clearInterval(gameLoop);
    };
  }, []); // Empty dependency array since we want this to run once

  return (
    <GameContainer>
      <GameCanvas 
        ref={canvasRef} 
        width={GRID_SIZE * CELL_SIZE} 
        height={GRID_SIZE * CELL_SIZE} 
      />
      <GameScore>
        Score: {score}
        {gameOver && <div>Game Over! Press ESC to close</div>}
      </GameScore>
    </GameContainer>
  );
};

export const Notepad = ({ 
  onClose, 
  isActive, 
  onActivate, 
  isMinimized, 
  isMaximized, 
  onMinimize, 
  onMaximize,
  content,
  onContentChange,
  onSaveFile,
  defaultWidth,
  defaultHeight,
  initialPosition
}) => {
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(0);
  const [showSnakeGame, setShowSnakeGame] = useState(false);
  const [wordCount, setWordCount] = useState(0);

  useEffect(() => {
    setWordCount(content.trim() ? content.trim().split(/\s+/).length : 0);
  }, [content]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && showSnakeGame) {
      setShowSnakeGame(false);
      return;
    }

    if (e.target.value.toLowerCase().includes('snake') && !showSnakeGame) {
      setShowSnakeGame(true);
    }
  };

  const themes = [
    {
      name: 'Classic',
      background: 'white',
      text: 'black',
      fontFamily: 'MS Sans Serif'
    },
    {
      name: 'Matrix',
      background: '#000',
      text: '#00ff00',
      fontFamily: 'Courier New',
      textShadow: '0 0 5px #00ff00',
      animation: 'matrix 2s infinite'
    },
    {
      name: 'Vaporwave',
      background: 'linear-gradient(45deg, #ff00ff, #00ffff)',
      text: '#ffffff',
      fontFamily: 'VT323',
      fontSize: '18px',
      textShadow: '2px 2px #ff00ff, -2px -2px #00ffff'
    },
    {
      name: 'Rainbow',
      background: '#000',
      text: '#fff',
      animation: 'rainbow 5s infinite'
    },
    {
      name: 'Glitch',
      background: '#0000aa',
      text: '#ffffff',
      fontFamily: 'VT323',
      textShadow: '2px 0 #ff0000, -2px 0 #00ff00'
    },
    {
      name: 'Disco',
      background: '#000',
      text: '#fff',
      animation: 'disco 1s infinite',
      textShadow: '0 0 5px #fff'
    }
  ];

  const handleSave = (filename) => {
    onSaveFile(filename, content);
    setShowSaveDialog(false);
  };

  const cycleTheme = () => {
    setCurrentTheme((prev) => (prev + 1) % themes.length);
  };

  return (
    <>
      <Window
        title="Notepad"
        onClose={onClose}
        isActive={isActive}
        onActivate={onActivate}
        isMinimized={isMinimized}
        isMaximized={isMaximized}
        onMinimize={onMinimize}
        onMaximize={onMaximize}
        initialPosition={initialPosition}
        defaultWidth={defaultWidth}
        defaultHeight={defaultHeight}
      >
        <MenuBar>
          <MenuItem 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            onMouseEnter={() => isMenuOpen && setIsMenuOpen(true)}
          >
            File
            <MenuDropdown isOpen={isMenuOpen}>
              <MenuOption onClick={() => {
                setShowSaveDialog(true);
                setIsMenuOpen(false);
              }}>
                Save As...
              </MenuOption>
            </MenuDropdown>
          </MenuItem>
          <MenuItem onClick={cycleTheme}>
            Theme ({themes[currentTheme].name})
          </MenuItem>
        </MenuBar>

        <TextArea 
          value={content}
          onChange={(e) => onContentChange(e.target.value)}
          onKeyDown={handleKeyDown}
          spellCheck="false"
          theme={themes[currentTheme]}
          placeholder=""
        />

        {showSnakeGame && (
          <SnakeGame onClose={() => setShowSnakeGame(false)} />
        )}

        <StatusBar>
          <div>Words: {wordCount}</div>
          <div>Theme: {themes[currentTheme].name}</div>
          <div>Press F1 for help</div>
        </StatusBar>
      </Window>
      {showSaveDialog && (
        <SaveDialog
          onClose={() => setShowSaveDialog(false)}
          onSave={handleSave}
          isActive={true}
          onActivate={() => {}}
        />
      )}
    </>
  );
}; 